/*
* Copyright (c) 2009-2021. Authors: see NOTICE file.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*      http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

import Vue from 'vue';
import {DEFAULT_FLUORESCENCE_COLORS, DEFAULT_FLUORESCENCE_MAX} from '@/utils/fluorescence.js';

const FLUORESCENCE_MODE_COMPOSITE = 'composite';
const FLUORESCENCE_MODE_SINGLE = 'single';

function defaultFluorescenceChannel(channel) {
  return {
    visible: true,
    color: DEFAULT_FLUORESCENCE_COLORS[channel % DEFAULT_FLUORESCENCE_COLORS.length],
    min: 0,
    max: DEFAULT_FLUORESCENCE_MAX
  };
}

function defaultSingleFluorescenceChannel() {
  return {
    min: 0,
    max: DEFAULT_FLUORESCENCE_MAX
  };
}

function ensureFluorescenceState(state) {
  if(!state.fluorescence.mode) {
    Vue.set(state.fluorescence, 'mode', FLUORESCENCE_MODE_COMPOSITE);
  }
  if(state.fluorescence.activeChannel == null) {
    Vue.set(state.fluorescence, 'activeChannel', 0);
  }
  if(!state.fluorescence.singleChannels) {
    Vue.set(state.fluorescence, 'singleChannels', {});
  }
}

function ensureFluorescenceChannel(state, channel) {
  ensureFluorescenceState(state);
  if(!state.fluorescence.channels[channel]) {
    Vue.set(state.fluorescence.channels, channel, defaultFluorescenceChannel(channel));
  }
}

function ensureSingleFluorescenceChannel(state, channel) {
  ensureFluorescenceState(state);
  if(!state.fluorescence.singleChannels[channel]) {
    Vue.set(state.fluorescence.singleChannels, channel, defaultSingleFluorescenceChannel());
  }
}

function clampIntensity(value) {
  let numericValue = Number(value);
  if(isNaN(numericValue)) {
    numericValue = 0;
  }
  numericValue = Math.round(numericValue);
  numericValue = Math.max(0, numericValue);
  numericValue = Math.min(255, numericValue);
  return numericValue;
}

function normalizedRange(range, min, max) {
  if(Array.isArray(range)) {
    min = range[0];
    max = range[1];
  }

  min = clampIntensity(min);
  max = clampIntensity(max);
  if(max <= min) {
    min = Math.min(min, 254);
    max = min + 1;
  }

  return {min, max};
}

function normalizedChannel(channel, nbChannels) {
  let numericChannel = Number(channel);
  if(isNaN(numericChannel)) {
    numericChannel = 0;
  }
  numericChannel = Math.max(0, Math.round(numericChannel));
  if(nbChannels != null) {
    numericChannel = Math.min(numericChannel, Math.max(0, Number(nbChannels) - 1));
  }
  return numericChannel;
}

export default {
  state() {
    return {
      brightness: 0,
      contrast: 0,
      hue: 0,
      saturation: 0,

      filter: null,

      fluorescence: {
        enabled: false,
        mode: 'composite',
        activeChannel: 0,
        channels: {},
        singleChannels: {}
      }
    };
  },

  mutations: {
    setBrightness(state, value) {
      state.brightness = value;
    },

    setContrast(state, value) {
      state.contrast = value;
    },

    setHue(state, value) {
      state.hue = value;
    },

    setSaturation(state, value) {
      state.saturation = value;
    },

    resetColorManipulation(state) {
      state.brightness = 0;
      state.contrast = 0;
      state.hue = 0;
      state.saturation = 0;
    },

    setFilter(state, filter) {
      state.filter = filter;
    },

    setFluorescenceEnabled(state, value) {
      ensureFluorescenceState(state);
      state.fluorescence.enabled = value;
    },

    setFluorescenceMode(state, mode) {
      ensureFluorescenceState(state);
      Vue.set(
        state.fluorescence,
        'mode',
        mode === FLUORESCENCE_MODE_SINGLE ? FLUORESCENCE_MODE_SINGLE : FLUORESCENCE_MODE_COMPOSITE
      );
    },

    setFluorescenceActiveChannel(state, {channel, nbChannels} = {}) {
      ensureFluorescenceState(state);
      let activeChannel = normalizedChannel(channel, nbChannels);
      ensureSingleFluorescenceChannel(state, activeChannel);
      Vue.set(state.fluorescence, 'activeChannel', activeChannel);
    },

    initializeFluorescenceChannels(state, nbChannels) {
      ensureFluorescenceState(state);

      for(let channel = 0; channel < nbChannels; channel++) {
        ensureFluorescenceChannel(state, channel);
        ensureSingleFluorescenceChannel(state, channel);
      }

      Object.keys(state.fluorescence.channels).forEach(channel => {
        if(Number(channel) >= nbChannels) {
          Vue.delete(state.fluorescence.channels, channel);
        }
      });

      Object.keys(state.fluorescence.singleChannels).forEach(channel => {
        if(Number(channel) >= nbChannels) {
          Vue.delete(state.fluorescence.singleChannels, channel);
        }
      });

      Vue.set(state.fluorescence, 'activeChannel', normalizedChannel(state.fluorescence.activeChannel, nbChannels));
    },

    setFluorescenceChannelVisible(state, {channel, visible}) {
      ensureFluorescenceChannel(state, channel);
      Vue.set(state.fluorescence.channels[channel], 'visible', visible);
    },

    setFluorescenceChannelColor(state, {channel, color}) {
      ensureFluorescenceChannel(state, channel);
      Vue.set(state.fluorescence.channels[channel], 'color', color);
    },

    setFluorescenceChannelRange(state, {channel, range, min, max}) {
      ensureFluorescenceChannel(state, channel);
      let normalized = normalizedRange(range, min, max);
      Vue.set(state.fluorescence.channels[channel], 'min', normalized.min);
      Vue.set(state.fluorescence.channels[channel], 'max', normalized.max);
    },

    setFluorescenceSingleChannelRange(state, {channel, range, min, max}) {
      ensureSingleFluorescenceChannel(state, channel);
      let normalized = normalizedRange(range, min, max);
      Vue.set(state.fluorescence.singleChannels[channel], 'min', normalized.min);
      Vue.set(state.fluorescence.singleChannels[channel], 'max', normalized.max);
    },

    resetFluorescenceChannels(state, nbChannels) {
      ensureFluorescenceState(state);
      Vue.set(state.fluorescence, 'channels', {});
      for(let channel = 0; channel < nbChannels; channel++) {
        Vue.set(state.fluorescence.channels, channel, defaultFluorescenceChannel(channel));
      }
    },

    resetFluorescenceSingleChannel(state, channel) {
      ensureFluorescenceState(state);
      channel = normalizedChannel(channel);
      Vue.set(state.fluorescence.singleChannels, channel, defaultSingleFluorescenceChannel());
    },

    resetFluorescenceSingleChannels(state, nbChannels) {
      ensureFluorescenceState(state);
      Vue.set(state.fluorescence, 'singleChannels', {});
      for(let channel = 0; channel < nbChannels; channel++) {
        Vue.set(state.fluorescence.singleChannels, channel, defaultSingleFluorescenceChannel());
      }
      Vue.set(state.fluorescence, 'activeChannel', normalizedChannel(state.fluorescence.activeChannel, nbChannels));
    }
  }
};
